<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="t('ff.page.title', 'Calcola la tua rata')" :subTitle="t('ff.page.subtitle', 'Forms')" />

    <WelcomeForfettario v-if="!showResultsArea" />

    <div id="wizard-form-row" style="margin-top: -25px;" class="row justify-content-center" v-if="!showResultsArea">
      <div class="col-lg-12">
        <FormWizard @submit-wizard="handleFormSubmission" :initial-data="wizardFormData" />
      </div>
    </div>

    <div id="results-display-row" class="row justify-content-center" v-else>
      <div class="col-lg-12">
        <div v-if="showAlert" class="alert alert-warning text-warning mb-4" role="alert">
          <i class="ri-error-warning-line fs-18 me-1"></i>
          <span v-html="alertMessage"></span>
        </div>
        <TaxResults
          :results="apiResults"
          :liquidityResults="liquidityApiResults"
          :contributionRegime="wizardFormData.contributionRegime"
          :formData="wizardFormData"
        />
        <!-- Final Informative Section -->
        <div class="card custom-shadow rounded-3 bg-hero-secondary  border mb-4">
          <div class="card-body p-4 text-white">
            <!-- Flex container for desktop, block for mobile -->
            <div class="d-lg-flex justify-content-lg-between align-items-lg-center">
              <!-- Text (left on desktop, top on mobile) -->
              <div class="text-center text-lg-start mb-3 mb-lg-0" style="max-width: 600px;">
                <h3 class="mb-0 fw-semibold text-white" v-html="t('ff.results.final_section.title', 'Il tuo piano è già al sicuro.')"></h3>
                <span class="d-block text-white" v-html="t('ff.results.final_section.text', 'Ottima notizia! Questa simulazione è stata salvata automaticamente nella tua dashboard. Puoi tornare qui in ogni momento per modificarla o creare nuovi scenari.')"></span>
              </div>

              <!-- Buttons (right on desktop, bottom on mobile) -->
              <div class="d-flex justify-content-center justify-content-lg-end gap-3 flex-shrink-0">
                <router-link to="/dashboard" class="btn btn-primary py-3 px-4 fw-semibold ">{{ t('ff.results.final_section.cta_button', 'Dashboard') }}</router-link>
                <button @click="editData" class="btn btn-outline-primary bg-white  text-primary py-3 px-4 fw-semibold border-0">{{ t('ff.button.edit', 'Modifica') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import PageTitle from "@/components/Common/PageTitle.vue";
import FormWizard from "@/components/Modules/Forms/Wizard/FormWizard.vue";
import { calculateTax } from "@/services/taxCalculationService";
import { calculateProgressiveLiquidity } from "@/services/progressiveLiquidityService"; // New import
import { authStore } from '@/stores/auth';
import { Modal } from 'bootstrap';
import TaxResults from "@/components/Modules/TaxCalculation/TaxResults.vue";
import WelcomeForfettario from "@/components/Modules/TaxCalculation/WelcomeForfettario.vue";
import { t } from "@/utils/i18nHelper";

export default defineComponent({
  name: "CalcoloForfettarioPage",
  components: {
    PageTitle,
    FormWizard,
    TaxResults,
    WelcomeForfettario,
  },
  setup() {
    const showResultsArea = ref(false);
    const apiResults = ref<any>(null);
    const liquidityApiResults = ref<any>(null); // New ref for liquidity results
    const wizardFormData = ref<any>({});

    const showAlert = ref(false);
    const alertMessage = ref('');

    const COMING_SOON_REGIMES = [
      // TODO
      'GESTIONE_SEPARATA_EX_INPGI',
      'IVS_COMMERCIANTI_ENASARCO',
    ];

    const COMING_SOON_CATEGORIES = [
      'GIORNALISTA',
      'INTERMEDIARIES',
    ];

    watch([() => wizardFormData.value.contributionRegime, () => wizardFormData.value.category], ([newRegime, newCategory]) => {
      if (COMING_SOON_REGIMES.includes(newRegime) || COMING_SOON_CATEGORIES.includes(newCategory)) {
        alertMessage.value = t('ff.alert.coming_soon', '<strong>Attenzione!</strong> Il regime contributivo o la categoria selezionata è "presto disponibile". Il calcolo potrebbe non essere preciso. Verrai contattato quando sarà ultimato.');
        showAlert.value = true;
      } else {
        showAlert.value = false;
        alertMessage.value = '';
      }
    }, { immediate: true }); // Immediate to check initial formData if already set

    return {
      showResultsArea,
      apiResults,
      liquidityApiResults, // Expose to template
      wizardFormData,
      showAlert, // Expose to template
      alertMessage, // Expose to template
      t,
    };
  },
  methods: {
    async handleFormSubmission(formData: any) {
      console.log("Dati del form ricevuti in CalcoloForfettarioPage:", formData);
      this.wizardFormData = formData;

      if (!authStore.isLoggedIn.value) {
        // ... (login logic remains the same)
        console.log("Utente non loggato. Attivo il popup di login.");

        const authModalElement = document.getElementById('authModal');
        if (authModalElement) {
          const authModal = new Modal(authModalElement);
          authModal.show();

          return new Promise<void>((resolve) => {
            const handleModalHidden = async () => {
              authModalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
              if (authStore.isLoggedIn.value) {
                console.log("Login riuscito dopo interazione modale. Ritento la sottomissione.");
                await this.handleFormSubmission(formData);
              } else {
                console.log("Modale chiusa senza login. Annullamento chiamata API.");
              }
              resolve();
            };
            authModalElement.addEventListener('hidden.bs.modal', handleModalHidden);
          });
        } else {
          console.error("AuthModal element not found. Cannot open login popup.");
          
          return;
        }
      }

      try {
        // API Call 1: Tax Calculation
        const taxResult = await calculateTax(formData);
        console.log("Risposta API Calcolo Tasse:", taxResult);
        this.apiResults = taxResult;
        this.showResultsArea = true; // Show results area even if liquidity fails

        // API Call 2: Progressive Liquidity
        try {
            const liquidityPayload = {
              action: "progressive_liquidity_scheduler",
              calculationData: taxResult,
              formData: formData,
              useSafetyMargin: formData.useSafetyMargin || false,
            };
            const liquidityResult = await calculateProgressiveLiquidity(liquidityPayload);
            console.log("Risposta API Liquidità:", liquidityResult);
            this.liquidityApiResults = liquidityResult;
        } catch (liquidityError: any) {
            this.liquidityApiResults = null; // Ensure it's null on failure
            console.error("Errore API Liquidità:", liquidityError.message);
            alert(t('ff.alert.liquidity_error', 'ATTENZIONE: Il calcolo delle tasse è riuscito, ma la pianificazione delle rate è fallita.\n\nErrore: {message}', { message: liquidityError.message }));
        }

      } catch (taxError: any) {
        console.error("Errore API Calcolo Tasse:", taxError.message);
        alert(t('ff.alert.tax_error', 'ERRORE: Il calcolo principale delle tasse è fallito: {message}', { message: taxError.message }));
      }
    },
    editData() {
      this.showResultsArea = false;
    }
  }
});
</script>
